import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { CreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CreditProgressBar";
import { ShieldIcon } from "@src/appV2/redesign/EmergencyCredits/components/ShieldIcon";
import {
  GOLDEN_RATIO_BOTTOM_FLEX,
  GOLDEN_RATIO_TOP_FLEX,
} from "@src/appV2/redesign/utils/goldenRatioLayout";

import { ScoreDisplay } from "./components/ScoreDisplay";
import { ScoreLineItemsContainer } from "./components/ScoreLineItemsContainer";
import { useClipboardScorePageState } from "./hooks/useClipboardScorePageState";
import { type YourClipboardScorePageProps } from "./types";

export function YourClipboardScorePage(props: YourClipboardScorePageProps) {
  const {
    startScore,
    scoreChanges,
    subtitle,
    doubleShiftNote,
    gradientVariant,
    showCreditProgressBar,
    creditProgressBarProps,
    creditAmount = 0,
    canUseCredit,
    onContinue,
    autoApplyCredit = false,
    isButtonDisabled = false,
  } = props;

  const {
    visibleLineItemCount,
    scoreDisplayProps,
    effectiveCreditProgressBarProps,
    creditLineItem,
    creditState,
    buttonText,
    buttonEnabled,
    handleButtonClick,
  } = useClipboardScorePageState({
    startScore,
    scoreChanges,
    creditAmount,
    canUseCredit,
    autoApplyCredit,
    creditProgressBarProps,
    isButtonDisabled,
    onContinue,
  });

  useLogEffect(APP_V2_USER_EVENTS.YOUR_CLIPBOARD_SCORE_PAGE_VIEWED, {
    startScore,
    scoreChanges,
    canUseCredit,
  });

  return (
    <PageWrapper
      sx={{
        background: (theme) => theme.palette.shift?.unverified?.gradient,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack sx={{ flex: 1 }}>
        <Box sx={{ flex: GOLDEN_RATIO_TOP_FLEX, minHeight: 0 }} />

        <Stack
          spacing={9}
          sx={{
            paddingTop: 2,
            paddingX: 6,
            textAlign: "center",
            alignItems: "center",
          }}
        >
          <Stack spacing={4} alignItems="center">
            <Text
              variant="h3"
              textAlign="center"
              sx={(theme) => ({
                paddingX: 6,
                color:
                  gradientVariant === "negative"
                    ? (theme.palette.intent?.error.text ?? theme.palette.error.main)
                    : theme.palette.text.secondary,
              })}
            >
              Your Clipboard Score
            </Text>

            <Text
              semibold
              variant="h6"
              textAlign="center"
              sx={(theme) => ({
                paddingX: 6,
                color: theme.palette.text.secondary,
              })}
            >
              {subtitle}
            </Text>

            {doubleShiftNote && (
              <Text
                variant="body2"
                textAlign="center"
                sx={(theme) => ({
                  paddingX: 6,
                  color: theme.palette.text.secondary,
                })}
              >
                {doubleShiftNote}
              </Text>
            )}
          </Stack>

          <Stack spacing={7} alignItems="center" sx={{ alignSelf: "stretch" }}>
            <Box sx={{ paddingY: 8 }}>
              <ScoreDisplay {...scoreDisplayProps} />
            </Box>

            {showCreditProgressBar && isDefined(effectiveCreditProgressBarProps) && (
              <CreditProgressBar {...effectiveCreditProgressBarProps} />
            )}
          </Stack>

          <ScoreLineItemsContainer
            startScore={startScore}
            scoreChanges={scoreChanges}
            visibleLineItemCount={visibleLineItemCount}
            creditLineItem={creditLineItem}
          />
        </Stack>

        <Box sx={{ flex: GOLDEN_RATIO_BOTTOM_FLEX, minHeight: 0 }} />
      </Stack>

      <DialogFooter orientation="horizontal">
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={!buttonEnabled}
          data-testid="your-clipboard-score-action-button"
          onClick={handleButtonClick}
        >
          {canUseCredit && !autoApplyCredit && creditState === "idle" ? (
            <Stack direction="row" alignItems="center" spacing={2}>
              <ShieldIcon
                variant={buttonEnabled ? "filled" : "outlined"}
                sx={{ width: 18, height: 18 }}
              />
              <Box component="span">{buttonText}</Box>
            </Stack>
          ) : (
            buttonText
          )}
        </Button>
      </DialogFooter>
    </PageWrapper>
  );
}
